import { Reveal } from "../Reveal";
import { Mail, Phone, MapPin } from "lucide-react";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="text-[11px] tracking-[0.3em] uppercase text-[#B8945F] font-medium">
                05 — Begin
              </span>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={100}>
              <h2
                data-testid="contact-heading"
                className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#0F1B3D] leading-[1.05] tracking-tight"
              >
                Let's <em className="font-light">build</em> your next collection.
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-6 max-w-xl text-[15px] md:text-base text-[#1A1A1A]/70 leading-relaxed">
                Tell us about your brand and the pieces you have in mind. We'll come back with reference imagery, leather options, indicative pricing and a sampling timeline — usually within two working days.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#0F1B3D]/10">
          <ContactCard
            Icon={Mail}
            label="Email"
            value="exports@shoozix.in"
            href="mailto:exports@shoozix.in"
            testid="contact-email"
          />
          <ContactCard
            Icon={Phone}
            label="Direct Line"
            value="+91 120 431 2742"
            href="tel:+911204312742"
            testid="contact-phone"
          />
          <ContactCard
            Icon={MapPin}
            label="Workshops"
            value="Agra & Noida, India"
            href="#atelier"
            testid="contact-workshops"
          />
        </div>

        {/* Inquiry form */}
        <Reveal delay={200}>
          <form
            data-testid="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const name = form.name?.value || "";
              window.location.href = `mailto:exports@shoozix.in?subject=Inquiry from ${encodeURIComponent(
                name
              )}&body=${encodeURIComponent(form.message?.value || "")}`;
            }}
            className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10"
          >
            <div className="md:col-span-1">
              <Field name="name" label="Your Name" testid="contact-form-name" />
            </div>
            <div className="md:col-span-1">
              <Field
                name="company"
                label="Brand / Company"
                testid="contact-form-company"
              />
            </div>
            <div className="md:col-span-1">
              <Field
                name="email"
                type="email"
                label="Email"
                testid="contact-form-email"
              />
            </div>
            <div className="md:col-span-1">
              <Field
                name="phone"
                label="Phone (optional)"
                testid="contact-form-phone"
              />
            </div>
            <div className="md:col-span-2">
              <Field
                name="message"
                label="Tell us about your project"
                multiline
                testid="contact-form-message"
              />
            </div>
            <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4 border-t border-[#0F1B3D]/10">
              <p className="text-[12px] tracking-wide text-[#0F1B3D]/60 max-w-md">
                By submitting, you'll start a direct email to our exports desk — no autoresponders, no junk.
              </p>
              <button
                type="submit"
                data-testid="contact-form-submit"
                className="group inline-flex items-center justify-center gap-3 bg-[#0F1B3D] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-[#B8945F] transition-colors duration-500"
              >
                <span>Send Inquiry</span>
                <span className="w-6 h-px bg-[#F9F8F6] group-hover:w-10 transition-all duration-500" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const ContactCard = ({ Icon, label, value, href, testid }) => (
  <Reveal>
    <a
      href={href}
      data-testid={testid}
      className="group block bg-[#F9F8F6] hover:bg-[#0F1B3D] hover:text-[#F9F8F6] transition-colors duration-700 p-10 md:p-12 h-full"
    >
      <div className="flex items-start justify-between mb-8">
        <Icon className="w-6 h-6 text-[#B8945F]" strokeWidth={1.25} />
        <span className="text-[10px] tracking-[0.3em] uppercase text-current/60">
          {label}
        </span>
      </div>
      <p className="font-serif text-2xl md:text-3xl text-current leading-tight">
        {value}
      </p>
      <div className="mt-6 h-px w-10 bg-[#B8945F] group-hover:w-24 transition-all duration-700" />
    </a>
  </Reveal>
);

const Field = ({ name, label, type = "text", multiline = false, testid }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.3em] uppercase text-[#0F1B3D]/60 mb-2">
      {label}
    </span>
    {multiline ? (
      <textarea
        name={name}
        rows={5}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#0F1B3D]/25 focus:border-[#B8945F] outline-none py-3 text-[15px] text-[#0F1B3D] placeholder:text-[#0F1B3D]/30 resize-none transition-colors"
      />
    ) : (
      <input
        type={type}
        name={name}
        data-testid={testid}
        className="w-full bg-transparent border-b border-[#0F1B3D]/25 focus:border-[#B8945F] outline-none py-3 text-[15px] text-[#0F1B3D] placeholder:text-[#0F1B3D]/30 transition-colors"
      />
    )}
  </label>
);
